import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Bell, Sparkle, Heart, ChatCircle, Moon } from "@phosphor-icons/react";
import { authFetch } from "../context/AuthContext";
import { formatDistanceToNow } from "date-fns";

const ICON_MAP = {
    sparkle: Sparkle,
    heart: Heart,
    chat: ChatCircle,
    moon: Moon,
};

const KIND_COLOR = {
    top_match: "text-gold",
    new_match: "text-rose-300",
    unread_message: "text-sky-300",
    moon_shift: "text-violet-300",
};

export default function NotificationCenter() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        authFetch("get", "/notifications")
            .then((r) => setItems(r.data?.notifications || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading || items.length === 0) return null;

    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass p-5 sm:p-6"
            data-testid="notification-center"
        >
            <div className="flex items-center gap-2 mb-4">
                <Bell size={14} weight="duotone" className="text-gold" />
                <p className="label-tracking text-gold">Cosmic Signals</p>
                <span className="text-xs text-muted ml-auto" data-testid="notification-count">
                    {items.length} new
                </span>
            </div>
            <ul className="space-y-2">
                {items.map((n) => {
                    const Icon = ICON_MAP[n.icon] || Bell;
                    const iconColor = KIND_COLOR[n.kind] || "text-gold";
                    let when = "";
                    try {
                        when = formatDistanceToNow(new Date(n.created_at), { addSuffix: true });
                    } catch { /* ignore parse err */ }
                    return (
                        <li key={n.id}>
                            <Link
                                to={n.href}
                                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-gold/30 transition-all group"
                                data-testid={`notification-item-${n.kind}`}
                            >
                                <div className={`w-9 h-9 rounded-full bg-[#0A0A1A] border border-white/10 flex items-center justify-center flex-shrink-0 ${iconColor}`}>
                                    <Icon size={14} weight="duotone" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm truncate group-hover:text-gold transition-colors">{n.title}</p>
                                    <p className="text-xs text-muted truncate">{n.subtitle}</p>
                                </div>
                                {when && (
                                    <span className="text-[10px] text-muted whitespace-nowrap flex-shrink-0 hidden sm:inline">
                                        {when}
                                    </span>
                                )}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </motion.section>
    );
}
